import { useEffect, useState, useRef } from 'react';
import api from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import WeightChart from '../components/WeightChart';
import { MEASUREMENT_LABELS, getUnit } from '../utils/dynamics';
import { Link } from 'react-router-dom';
import ProgressCard from '../components/ProgressCard';

function useCountUp(target, dur=800) {
  const [v,setV] = useState(0);
  const r = useRef();
  useEffect(() => {
    if (!target && target!==0) return;
    const abs = Math.abs(target);
    const s = performance.now();
    const tick = now => {
      const p = Math.min((now-s)/dur,1), e=1-Math.pow(1-p,3);
      setV(Math.round(abs*e*10)/10);
      if(p<1) r.current=requestAnimationFrame(tick);
    };
    r.current=requestAnimationFrame(tick);
    return ()=>cancelAnimationFrame(r.current);
  },[target]);
  return v;
}

// Для каждого поля: уменьшение = хорошо?
const GOOD_DOWN = ['weight_kg','waist_cm','abdomen_cm','hips_cm','thigh_cm','above_knee_cm','calf_cm'];

function DeltaCard({ field, label, first, last, acc, isDark, delay=0 }) {
  const delta = (first!=null && last!=null) ? Math.round((last-first)*10)/10 : null;
  const animated = useCountUp(delta!=null ? Math.abs(delta) : 0);
  const unit = getUnit(field);

  const goodDown = GOOD_DOWN.includes(field);
  const isGood = delta===null ? null : (goodDown ? delta<0 : delta>0);
  const isNeutral = delta===0;

  const card = isDark ? '#1A1526' : '#FFFFFF';
  const bdr  = isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.06)';
  const txt  = isDark ? '#F2EEF9' : '#2D1B35';
  const mut  = isDark ? 'rgba(200,185,220,.55)' : 'rgba(80,60,100,.5)';

  const COLOR_FIELDS = ['weight_kg', 'waist_cm', 'abdomen_cm'];
  const useColor = COLOR_FIELDS.includes(field);
  const color = useColor ? (isNeutral ? txt : (isGood ? '#22C55E' : '#EF4444')) : txt;
  const bgColor = 'transparent';
  const icon = isNeutral ? '→' : (delta<0 ? '↓' : '↑');

  return (
    <div style={{
      background:card, border:`1px solid ${bdr}`, borderRadius:16, padding:'14px 16px',
      boxShadow:isDark?'0 2px 12px rgba(0,0,0,.3)':'0 2px 12px rgba(100,60,140,.07)',
      animation:`_pin .4s cubic-bezier(.16,1,.3,1) ${delay}s both`,
    }}>
      <p style={{ color:mut, fontSize:'.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', margin:'0 0 8px' }}>{label}</p>
      {delta!==null ? (
        <>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
            <span style={{ fontFamily:'Nunito,system-ui', fontSize:'1.7rem', fontWeight:800, color }}>
              {icon}{animated}
            </span>
            <span style={{ color:mut, fontSize:'.8rem' }}>{unit}</span>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:'.72rem' }}>
            <span style={{ color:mut }}>Было: <strong style={{color:txt}}>{first} {unit}</strong></span>
            <span style={{ color:mut }}>Стало: <strong style={{color:txt}}>{last} {unit}</strong></span>
          </div>
        </>
      ) : (
        <p style={{ color:mut, fontSize:'.82rem', margin:0 }}>Нет данных</p>
      )}
    </div>
  );
}

export default function Progress() {
  const { isDark, theme } = useTheme();
  const [measurements, setMeasurements] = useState([]);
  const [profile,      setProfile]      = useState(null);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    Promise.all([api.get('/measurements/with-dynamics'), api.get('/users/profile')])
      .then(([m, p]) => { setMeasurements(m.data.measurements || []); setProfile(p.data); })
      .finally(() => setLoading(false));
  }, []);

  const acc  = theme.accent;
  const glow = theme.glow;
  const txt  = isDark ? '#F2EEF9' : '#2D1B35';
  const mut  = isDark ? 'rgba(200,185,220,.6)' : 'rgba(80,60,100,.55)';
  const card = isDark ? '#1A1526' : '#FFFFFF';
  const bdr  = isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.06)';

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', padding:'4rem' }}>
      <div style={{ width:32, height:32, border:`3px solid ${acc}30`, borderTopColor:acc, borderRadius:'50%', animation:'_spin .8s linear infinite' }}/>
      <style>{'@keyframes _spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );

  if (measurements.length === 0) return (
    <div style={{ maxWidth:480, margin:'3rem auto', textAlign:'center' }}>
      <p style={{ fontSize:'3rem', margin:'0 0 12px' }}>📊</p>
      <h2 style={{ color:txt, fontFamily:'Nunito,system-ui', fontWeight:700, fontSize:'1.2rem', margin:'0 0 8px' }}>Нет замеров</h2>
      <p style={{ color:mut, fontSize:'.875rem', margin:'0 0 20px' }}>Добавь хотя бы один замер чтобы увидеть прогресс</p>
      <Link to="/measurements" style={{ display:'inline-block', padding:'.7rem 1.5rem', borderRadius:10, background:acc, color:'#fff', textDecoration:'none', fontWeight:700, boxShadow:`0 4px 16px ${glow}` }}>
        Добавить замер →
      </Link>
    </div>
  );

  // Хронологически: первый и последний
  const sorted  = [...measurements].sort((a,b) => new Date(a.measured_at)-new Date(b.measured_at));
  const first   = sorted[0];
  const latest  = sorted[sorted.length-1];
  const hasTwo  = sorted.length >= 2;
  const days    = hasTwo ? Math.round((new Date(latest.measured_at)-new Date(first.measured_at))/(24*3600*1000)) : 0;

  const KEY_DELTAS = [
    { field:'weight_kg',  label:'Вес' },
    { field:'waist_cm',   label:'Талия' },
    { field:'abdomen_cm', label:'Живот' },
    { field:'hips_cm',    label:'Бёдра' },
    { field:'thigh_cm',   label:'Бедро' },
    { field:'calf_cm',    label:'Голень' },
  ];

  return (
    <div style={{ maxWidth:720, margin:'0 auto', fontFamily:'Inter,system-ui,sans-serif' }}>
      <style>{`
        @keyframes _pin{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
        @keyframes _spin{to{transform:rotate(360deg)}}
        .pgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
        @media(max-width:600px){.pgrid{grid-template-columns:repeat(2,1fr)!important}}
      `}</style>

      {/* Header */}
      <div style={{ marginBottom:24, animation:'_pin .35s cubic-bezier(.16,1,.3,1) both' }}>
        <h1 style={{ color:txt, fontFamily:'Nunito,system-ui', fontWeight:800, fontSize:'1.6rem', margin:'0 0 4px', letterSpacing:-.5 }}>
          Прогресс
        </h1>
        <p style={{ color:mut, fontSize:'.82rem', margin:0 }}>
          {hasTwo
            ? `${sorted.length} замеров за ${days} дней · ${new Date(first.measured_at).toLocaleDateString('ru-RU',{day:'numeric',month:'long'})} → ${new Date(latest.measured_at).toLocaleDateString('ru-RU',{day:'numeric',month:'long'})}`
            : 'Добавь ещё один замер чтобы увидеть динамику'}
        </p>
      </div>

      {/* Итог от первого к последнему */}
      {hasTwo && (
        <div style={{ marginBottom:24 }}>
          <p style={{ color:mut, fontSize:'.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', margin:'0 0 12px' }}>
            Итого с первого замера
          </p>
          <div className="pgrid">
            {KEY_DELTAS.map(({field,label},i) => (
              <DeltaCard key={field} field={field} label={label}
                first={first[field]?parseFloat(first[field]):null}
                last={latest[field]?parseFloat(latest[field]):null}
                acc={acc} isDark={isDark} delay={i*.05}
              />
            ))}
          </div>
        </div>
      )}

      {/* Лучший результат */}
      {hasTwo && (() => {
        const bestField = KEY_DELTAS
          .filter(({field}) => GOOD_DOWN.includes(field) && first[field] && latest[field])
          .sort((a,b) => (parseFloat(first[b.field])-parseFloat(latest[b.field])) - (parseFloat(first[a.field])-parseFloat(latest[a.field])))[0];
        if (!bestField) return null;
        const diff = Math.round((parseFloat(first[bestField.field])-parseFloat(latest[bestField.field]))*10)/10;
        if (diff<=0) return null;
        return (
          <div style={{ background:`${acc}15`, border:`1.5px solid ${acc}30`, borderRadius:16, padding:'14px 18px', marginBottom:24, display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:'1.5rem' }}>🏆</span>
            <div>
              <p style={{ color:txt, fontWeight:700, fontSize:'.9rem', margin:'0 0 2px' }}>Лучший результат</p>
              <p style={{ color:mut, fontSize:'.82rem', margin:0 }}>
                {bestField.label} −{diff} {getUnit(bestField.field)} за {days} дней
              </p>
            </div>
          </div>
        );
      })()}

      {/* Графики */}
      {hasTwo && (
        <div style={{ marginBottom:24 }}>
          <p style={{ color:mut, fontSize:'.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', margin:'0 0 12px' }}>
            Динамика по времени
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[
              { field:'weight_kg', label:'Вес, кг', color:acc },
              { field:'waist_cm',  label:'Талия, см', color:'#9B6FD4' },
              { field:'abdomen_cm',label:'Живот, см', color:'#00CFDB' },
            ].filter(({field}) => sorted.some(m=>m[field])).map(({field,label,color}) => (
              <WeightChart key={field} data={[...measurements].sort((a,b)=>new Date(a.measured_at)-new Date(b.measured_at))} field={field} label={label} color={color}/>
            ))}
          </div>
        </div>
      )}

      {/* Instagram card */}
      {hasTwo && <ProgressCard measurements={measurements} user={profile?.user}/>}

      {/* Ещё только 1 замер */}
      {!hasTwo && (
        <div style={{ background:card, border:`1px solid ${bdr}`, borderRadius:16, padding:'2.5rem', textAlign:'center', marginTop:16 }}>
          <p style={{ fontSize:'2rem', margin:'0 0 10px' }}>📈</p>
          <p style={{ color:txt, fontWeight:600, margin:'0 0 6px' }}>Добавь ещё один замер</p>
          <p style={{ color:mut, fontSize:'.85rem', margin:'0 0 20px', lineHeight:1.5 }}>
            После второго замера появятся графики и сравнение с первым замером
          </p>
          <Link to="/measurements" style={{ display:'inline-block', padding:'.65rem 1.4rem', borderRadius:10, background:acc, color:'#fff', textDecoration:'none', fontWeight:700, boxShadow:`0 4px 14px ${glow}` }}>
            + Добавить замер
          </Link>
        </div>
      )}
    </div>
  );
}
